import React, { useState, useEffect } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { Button, Form, Card } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faSave, faMapMarkerAlt, faBoxOpen } from "@fortawesome/free-solid-svg-icons";
import useFetch from "../../../hooks/useFetch";
import Swal from "sweetalert2";
import CreatableSelect from 'react-select/creatable';

export default function Localizacao() {
    const navigate = useNavigate();
    const { get, post, put } = useFetch();
    const location = useLocation();
    const { id } = useParams();

    const [state, setState] = useState({
        acao: location.state?.acao || "Visualizar",
        localizacao: { id_localizacao: "", sala: "", estante: "", prateleira: "", caixa: "", envelope_pasta: "" },
        opcoesLocalizacao: { salas: [], estantes: [], prateleiras: [] },
    });

    useEffect(() => {
        const fetchData = async () => {
            let acao = location.state?.acao || "Visualizar";
            let localizacao = { ...state.localizacao };

            if (id !== "criar") {
                const response = await get("/localizacao/" + id);
                if (response.ok) localizacao = response.result.data;
            }

            const resDistinct = await get("/distinct_localizacao");
            let opcoes = { salas: [], estantes: [], prateleiras: [] };
            if (resDistinct.ok) {
                opcoes.salas = resDistinct.result.data.salas.map(s => ({ value: s, label: s }));
            }

            setState(prev => ({ ...prev, acao, localizacao, opcoesLocalizacao: opcoes }));
        };
        fetchData();
    }, [id]);

    const changeSala = (newValue) => {
        setState(prev => ({ ...prev, localizacao: { ...prev.localizacao, sala: newValue ? newValue.value : "" } }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setState(prev => ({ ...prev, localizacao: { ...prev.localizacao, [name]: value } }));
    };

    async function salvarLocalizacao() {
        // POST /localizacao (sem id) pra criar — só PUT /localizacao/:id existe
        // pra editar. Antes isso sempre chamava POST /localizacao/0 mesmo ao
        // criar, o que não bate com nenhuma rota do backend (só há POST
        // /localizacao e PUT/DELETE /localizacao/:id) e caía no handler de 404
        // genérico do Express, devolvendo "Erro interno" pro usuário.
        const response = id === "criar"
            ? await post("/localizacao", state.localizacao)
            : await put(`/localizacao/${id}`, state.localizacao);

        if (response.ok) {
            Swal.fire({ title: "Sucesso!", text: "Localização salva.", icon: "success" });
            navigate("/admin/localizacoes");
        } else {
            Swal.fire({ title: "Erro", text: response.result?.message || "Erro ao salvar.", icon: "error" });
        }
    }

    // O <body> global do site define `color: #fff` (pensado pro tema escuro do
    // site público). Sem sobrescrever aqui, as opções do dropdown herdavam
    // esse branco e ficavam ilegíveis (texto branco em fundo quase branco).
    const customStyles = {
        control: (base, state) => ({
            ...base, backgroundColor: '#f8fafc', borderRadius: '10px', minHeight: '44px',
            borderColor: state.isFocused ? '#587b7f' : '#e2e8f0', boxShadow: 'none'
        }),
        menu: (base) => ({ ...base, zIndex: 5 }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#587b7f' : state.isFocused ? '#e8f0f1' : '#fff',
            color: state.isSelected ? '#fff' : '#2c3e50',
        }),
        singleValue: (base) => ({ ...base, color: '#2c3e50' }),
        input: (base) => ({ ...base, color: '#2c3e50' }),
        placeholder: (base) => ({ ...base, color: '#6c757d' }),
    };

    return (
        <div className="admin-page-container">
            <div className="mb-4">
                <Link to="/admin/localizacoes" className="btn-back-modern">
                    <FontAwesomeIcon icon={faAngleLeft} className="me-2" /> Voltar às Localizações
                </Link>
            </div>

            <Card className="filter-card-admin">
                <Card.Body className="p-4 p-md-5">
                    <div className="section-title-modern mb-5">
                        <div className="title-accent"></div>
                        <div>
                            <h2 className="admin-title">{state.acao} Localização</h2>
                            <p className="admin-subtitle small mb-0">Gestão de endereçamento físico do acervo</p>
                        </div>
                    </div>

                    <Form>
                        {/* GRUPO 1: LOCALIZAÇÃO MACRO */}
                        <div className="form-section-header mb-4">
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-teal" />
                            <span>Espaço Físico (Macro)</span>
                        </div>
                        <div className="row g-4 mb-5">
                            <Form.Group className="col-md-12">
                                <Form.Label className="label-admin">* Sala / Setor</Form.Label>
                                <CreatableSelect
                                    isClearable
                                    placeholder="Selecione ou digite uma nova sala..."
                                    options={state.opcoesLocalizacao.salas}
                                    value={state.localizacao.sala ? { label: state.localizacao.sala, value: state.localizacao.sala } : null}
                                    onChange={changeSala}
                                    styles={customStyles}
                                    isDisabled={state.acao === "Visualizar"}
                                />
                            </Form.Group>
                            <Form.Group className="col-md-6">
                                <Form.Label className="label-admin">Estante / Armário</Form.Label>
                                <Form.Control className="input-moderno" name="estante" value={state.localizacao.estante} onChange={handleChange} disabled={state.acao === "Visualizar"} />
                            </Form.Group>
                            <Form.Group className="col-md-6">
                                <Form.Label className="label-admin">Prateleira / Gaveta</Form.Label>
                                <Form.Control className="input-moderno" name="prateleira" value={state.localizacao.prateleira} onChange={handleChange} disabled={state.acao === "Visualizar"} />
                            </Form.Group>
                        </div>

                        {/* GRUPO 2: ARMAZENAMENTO ICONOGRÁFICO (MICRO) */}
                        <div className="form-section-header mb-4">
                            <FontAwesomeIcon icon={faBoxOpen} className="me-2 text-teal" />
                            <span>Armazenamento Detalhado (Micro)</span>
                        </div>
                        <div className="row g-4">
                            <Form.Group className="col-md-6">
                                <Form.Label className="label-admin">Caixa / Contentor</Form.Label>
                                <Form.Control 
                                    className="input-moderno" 
                                    name="caixa" 
                                    placeholder="Ex: CX-01"
                                    value={state.localizacao.caixa} 
                                    onChange={handleChange} 
                                    disabled={state.acao === "Visualizar"} 
                                />
                            </Form.Group>
                            <Form.Group className="col-md-6">
                                <Form.Label className="label-admin">Envelope / Pasta</Form.Label>
                                <Form.Control 
                                    className="input-moderno" 
                                    name="envelope_pasta" 
                                    placeholder="Ex: Pasta Azul / Envelope 10"
                                    value={state.localizacao.envelope_pasta} 
                                    onChange={handleChange} 
                                    disabled={state.acao === "Visualizar"} 
                                />
                            </Form.Group>
                        </div>

                        {state.acao !== "Visualizar" && (
                            <div className="d-flex justify-content-end mt-5 pt-4 border-top">
                                <Button className="btn-apply-filters px-5" onClick={salvarLocalizacao}>
                                    <FontAwesomeIcon icon={faSave} className="me-2" /> Salvar Localização
                                </Button>
                            </div>
                        )}
                    </Form>
                </Card.Body>
            </Card>
        </div>
    );
}